'use client';

import { useCallback, useMemo, useState } from 'react';

import { trackError } from '@libs/analytics';
import { getCustomerBookingEligibility, getPetBookingEligibility } from '@libs/api-proxy-client';
import { logSentryError, SentryAcpServicesGroomingErrors as SentryErrors } from '@libs/common-next';
import type { CustomerEligibilityResponse } from '@libs/common-types';

import { useFetchWithSnackbarErrorHandling } from './useFetchWithSnackbarErrorHandling';

export interface UseBookingEligibilityReturn {
    customerEligibility: Nullable<CustomerEligibilityResponse['result']>;
    eligibilityFetching: boolean;
    fetchCustomerEligibility: () => void;
    checkBookingEligibility: (petId: number, onRestriction: () => void) => Promise<boolean>;
    fetchCustomerAndPetEligibility: (petId: number, onRestriction?: () => void) => Promise<boolean>;
}

export function useBookingEligibility(): UseBookingEligibilityReturn {
    const { showApiErrorSnackbar, getErrorStatus, isServerError } = useFetchWithSnackbarErrorHandling();

    const [customerEligibility, setCustomerEligibility] =
        useState<Nullable<CustomerEligibilityResponse['result']>>(null);
    const [customerPetEligibility, setCustomerPetEligibility] =
        useState<Nullable<{ [x: string]: Record<string, unknown> }>>(null);
    const [eligibilityFetching, setEligibilityFetching] = useState(false);

    const getCustomerPetEligibility = useCallback(
        async (petId: number) => {
            try {
                // Check cache in state to avoid doing multiple requests for same petId
                // If cache exists return it
                if (customerPetEligibility?.[petId]) {
                    return customerPetEligibility[petId];
                }

                const result = await getPetBookingEligibility(petId.toString());

                // Write cache and return using functional update to avoid stale closure
                if (result?.result) {
                    setCustomerPetEligibility(prev => ({
                        ...prev,
                        ...result.result,
                    }));

                    return result.result?.[petId];
                }

                return null;
            } catch (error) {
                if (isServerError(getErrorStatus(error))) {
                    showApiErrorSnackbar();
                }
                logSentryError(error, SentryErrors.AppointmentsPage.GetCustomerPetEligibilityFailed);
                return null;
            }
        },
        [customerPetEligibility]
    );

    const checkBookingEligibility = useCallback(
        async (petId: number, onRestriction: () => void): Promise<boolean> => {
            // If customer non eligible we just open the modal without checking the pet
            if (!customerEligibility?.canBook) {
                onRestriction();
                return false;
            }

            // Check pet eligibility and open modal is not eligible
            const petEligibility = await getCustomerPetEligibility(petId);
            if (!petEligibility?.canBook) {
                onRestriction();
                return false;
            }
            return true;
        },
        [customerEligibility, getCustomerPetEligibility]
    );

    const fetchCustomerEligibility = useCallback(async () => {
        setEligibilityFetching(true);

        try {
            const result = await getCustomerBookingEligibility();
            if (result?.result) {
                setCustomerEligibility(result.result);
            }
        } catch (error) {
            if (isServerError(getErrorStatus(error))) {
                showApiErrorSnackbar();
            }
            trackError('appointments', error as string);
            logSentryError(error, SentryErrors.AppointmentsPage.GetEligibilityFailed);
        } finally {
            setEligibilityFetching(false);
        }
    }, []);

    const fetchCustomerAndPetEligibility = useCallback(
        async (petId: number, onRestriction?: () => void) => {
            const customerEligibility = await getCustomerBookingEligibility();
            if (customerEligibility?.result?.canBook) {
                const petEligibility = await getCustomerPetEligibility(petId);
                if (!petEligibility?.canBook) {
                    onRestriction?.();
                    return false;
                }
                return true;
            }
            return false;
        },
        [getCustomerPetEligibility]
    );

    return useMemo(
        () => ({
            customerEligibility,
            eligibilityFetching,
            fetchCustomerEligibility,
            checkBookingEligibility,
            fetchCustomerAndPetEligibility,
        }),
        [
            customerEligibility,
            eligibilityFetching,
            fetchCustomerEligibility,
            checkBookingEligibility,
            fetchCustomerAndPetEligibility,
        ]
    );
}
